import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Rocket, Stars } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import { authApi, setAuthToken } from '../services/api';
import { analytics } from '../services/analytics';
import { useNamespacedTranslation } from '../i18n/index';
import { MixedText } from '../utils/rtl';
import SpaceBackground from './layout/SpaceBackground';
import { AuthContainer } from './layout/PageContainer';
import { SpaceCard, Card, CardHeader, CardTitle, CardContent, CardFooter } from './ui/Card';
import { Button } from './ui/Button';
import { LoadingSpinner } from './ui/LoadingSpinner';

const Login: React.FC = () => {
  const { t, i18n } = useNamespacedTranslation('auth');
  const navigate = useNavigate();
  const isRTL = i18n.language === 'he';
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const isGoogleEnabled = Boolean(googleClientId);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResendLink, setShowResendLink] = useState(false);
  const [showRegisterSuggestion, setShowRegisterSuggestion] = useState(false);
  const authInProgress = loading || googleLoading;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Don't clear error immediately - let user see what they need to fix
  };

  const completeLogin = async (accessToken: string, trackedEmail?: string) => {
    setAuthToken(accessToken);
    
    const user = await authApi.getCurrentUser();
    localStorage.setItem('user', JSON.stringify(user));
    
    analytics.trackUserLoggedIn(trackedEmail || user.email);
    analytics.identify(user.id.toString(), {
      email: user.email,
      signup_date: user.created_at,
    });
    
    window.dispatchEvent(new Event('userLoggedIn'));
    navigate('/');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await authApi.login(formData);
      await completeLogin(response.access_token, formData.email);
    } catch (err: any) {
      analytics.trackError('Login Failed', { 
        email: formData.email,
        error: err.response?.data?.detail 
      });
      
      const errorDetail = err.response?.data?.detail;
      const errorStatus = err.response?.status;
      
      if (errorDetail?.includes('אנא אשרו את כתובת האימייל')) {
        setError(t('login.errors.email_not_verified'));
        setShowResendLink(true);
        setShowRegisterSuggestion(false);
      } else if (errorDetail === 'USER_NOT_FOUND' || errorStatus === 404) {
        setError(t('login.errors.user_not_found'));
        setShowResendLink(false);
        setShowRegisterSuggestion(true);
      } else {
        setError(errorDetail || t('login.errors.invalid_credentials'));
        setShowResendLink(false);
        setShowRegisterSuggestion(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => handleGoogleSuccess(tokenResponse.access_token),
    onError: () => handleGoogleError(),
  });

  const handleGoogleSuccess = async (credential: string) => {
    setGoogleLoading(true);
    setError(null);
    setShowResendLink(false);
    setShowRegisterSuggestion(false);

    try {
      const response = await authApi.loginWithGoogle({ credential });
      await completeLogin(response.access_token);
    } catch (err: any) {
      analytics.trackError('Google Login Failed', { 
        email: formData.email,
        error: err.response?.data?.detail 
      });

      const errorDetail = err.response?.data?.detail;
      if (errorDetail === 'GOOGLE_AUTH_NOT_CONFIGURED') {
        setError(t('login.errors.google_not_configured'));
      } else {
        setError(t('login.errors.google_failed'));
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError(t('login.errors.google_failed'));
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Space Background - Always visible */}
      <SpaceBackground theme="mixed" intensity="cosmic" />
      
      {/* Floating Elements for Extra Magic */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 animate-float">
          <Rocket className="w-12 h-12 text-vibrant-orange/30" />
        </div>
        <div className="absolute top-32 right-20 animate-float-delayed">
          <Stars className="w-8 h-8 text-muted-purple/40" />
        </div>
        <div className="absolute bottom-32 left-20 animate-pulse">
          <div className="w-4 h-4 bg-vibrant-orange/50 rounded-full"></div>
        </div>
      </div>

      {/* Main Content */}
      <AuthContainer className="relative z-10">
        <div className="max-w-md w-full mx-auto space-y-8">
          {/* Header */}
          <div className="text-center space-y-6">
            {/* Logo with glow effect */}
            <div className="flex justify-center">
              <div className="relative group">
                <div className="absolute inset-0 bg-vibrant-orange/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300 animate-pulse"></div>
                <img 
                  src="/study-bel-v2.png" 
                  alt="StudyBel Logo" 
                  className="relative w-20 h-20 object-contain group-hover:scale-110 transition-transform duration-300"
                />
              </div>
            </div>
            
            {/* Welcome Text */}
            <div className="space-y-3">
              <h1 className="text-4xl md:text-5xl font-bold text-bg-primary text-center">
                {isRTL ? (
                  <MixedText className="text-4xl md:text-5xl font-bold text-bg-primary">
                    {`${t('login.title')} `}
                  </MixedText>
                ) : (
                  <>{t('login.title')}</>
                )}
              </h1>
            </div>
          </div>

          {/* Login Form */}
          <SpaceCard 
            constellation="orion" 
            className="group hover:shadow-2xl transition-all duration-500 transform hover:scale-[1.02]"
          >
            <CardHeader>
              <CardTitle className={`text-center text-xl ${isRTL ? 'text-right' : ''}`}>
                {t('login.form.welcome_back')}
              </CardTitle>
            </CardHeader>

            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-6">
                {/* Error Message */}
                {error && (
                  <Card className="border-vibrant-orange/50 bg-vibrant-orange/10 animate-shake">
                    <CardContent className="text-vibrant-orange text-center py-3">
                      ⚠️ {error}
                      {showResendLink && (
                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={() => navigate('/verify-email')}
                            className="text-sm text-vibrant-orange hover:text-vibrant-orange/80 transition-colors duration-200 underline"
                          >
                            <MixedText>{t('verify_email.links.resend_verification')}</MixedText>
                          </button>
                        </div>
                      )}
                      {showRegisterSuggestion && (
                        <div className="mt-3 space-y-2">
                          <p className="text-sm">
                            <MixedText>{t('login.errors.register_suggestion')}</MixedText>
                          </p>
                          <button
                            type="button"
                            onClick={() => navigate('/register')}
                            className="text-sm bg-vibrant-orange/20 hover:bg-vibrant-orange/30 text-vibrant-orange px-3 py-1 rounded transition-colors duration-200 border border-vibrant-orange/30 hover:border-vibrant-orange/50"
                          >
                            <MixedText>{t('login.errors.register_link')}</MixedText>
                          </button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Email Field */}
                <div className="space-y-2">
                  <label 
                    htmlFor="email" 
                    className={`block text-sm font-medium text-surface-secondary ${isRTL ? 'text-right' : ''}`}
                  >
                    {t('login.form.email')}
                  </label>
                  <div className="relative group">
                    <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none transition-colors duration-200`}>
                      <Mail className="h-5 w-5 text-surface-muted group-focus-within:text-vibrant-orange" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className={`block w-full ${isRTL ? 'pr-10 pl-3 text-right' : 'pl-10 pr-3'} py-3 border-2 border-surface-muted/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-vibrant-orange/50 focus:border-vibrant-orange transition-all duration-200 bg-surface/80 backdrop-blur-sm text-surface-primary placeholder:text-surface-muted hover:border-surface-muted/50`}
                      placeholder={t('login.form.email_placeholder')}
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <label 
                    htmlFor="password" 
                    className={`block text-sm font-medium text-surface-secondary ${isRTL ? 'text-right' : ''}`}
                  >
                    {t('login.form.password')}
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none transition-colors duration-200">
                      <Lock className="h-5 w-5 text-surface-muted group-focus-within:text-vibrant-orange" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-10 py-3 border-2 border-surface-muted/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-vibrant-orange/50 focus:border-vibrant-orange transition-all duration-200 bg-surface/80 backdrop-blur-sm text-surface-primary placeholder:text-surface-muted hover:border-surface-muted/50"
                      placeholder={t('login.form.password_placeholder')}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-surface-muted hover:text-vibrant-orange transition-colors duration-200"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

              </CardContent>

              <CardFooter className="flex flex-col space-y-4">
                <div className="w-full max-w-[420px] mx-auto space-y-3">
                  {/* Login Button */}
                  <Button
                    type="submit"
                    variant="gradient"
                    size="lg"
                    className="w-full"
                    disabled={authInProgress}
                    glow
                  >
                    {authInProgress ? (
                      <div className="flex items-center justify-center space-x-2">
                        <LoadingSpinner variant="astronaut" size="lg" />
                        <span>🚀 {t('login.form.logging_in')}</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <span>🚀 {t('login.form.submit')}</span>
                      </div>
                    )}
                  </Button>

                  {/* Google Login below primary button */}
                  {isGoogleEnabled ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="lg"
                      className="w-full bg-white hover:bg-gray-100 text-gray-800 border border-gray-200"
                      onClick={() => googleLogin()}
                      disabled={authInProgress}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            fill="#4285F4"
                          />
                          <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                          />
                          <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            fill="#FBBC05"
                          />
                          <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                          />
                        </svg>
                        <MixedText>{t('login.google.button') || 'Sign in with Google'}</MixedText>
                      </div>
                    </Button>
                  ) : (
                    <div className="text-center text-sm text-surface-secondary bg-surface-muted/20 border border-dashed border-surface-muted/40 rounded-lg py-3">
                      <MixedText>{t('login.errors.google_not_configured')}</MixedText>
                    </div>
                  )}

                  {googleLoading && (
                    <div className="flex items-center justify-center gap-2 text-sm text-surface-secondary">
                      <LoadingSpinner variant="simple" size="sm" color="orange" />
                      <MixedText>{t('login.google.loading')}</MixedText>
                    </div>
                  )}
                </div>

                {/* Forgot Password Link */}
                <div className="w-full text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-vibrant-orange hover:text-vibrant-orange/80 transition-colors duration-200 underline"
                  >
                    <MixedText>{t('login.links.forgot_password')}</MixedText>
                  </button>
                </div>

                {/* Register Section */}
                <div className="w-full text-center space-y-3">
                  <p className="text-sm text-surface-secondary">
                    <MixedText>{t('login.links.register_text')}</MixedText>
                  </p>
                  {/* Register Button */}
                  <Button
                    variant="cosmic"
                    size="md"
                    className="w-full"
                    onClick={() => navigate('/register')}
                  >
                    {t('login.links.register_button')}
                  </Button>
                </div>
              </CardFooter>
            </form>
          </SpaceCard>
        </div>
      </AuthContainer>
    </div>
  );
};

export default Login; 
